import { api } from "@Aspecto/backend/convex/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useAction } from "convex/react";
import { AlertCircle, CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";

import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/invite/$token")({
  component: InvitePage,
});

function InvitePage() {
  const { token } = Route.useParams();
  const { data: invitation } = useSuspenseQuery(
    convexQuery(api.invitations.getInvitationByToken, { token }),
  );
  const getClerkUrl = useAction(api.invitations.getClerkInviteUrlByToken);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (
      !invitation ||
      invitation.isExpired ||
      invitation.status === "expired" ||
      invitation.status === "accepted" ||
      invitation.status === "revoked"
    ) {
      return;
    }

    getClerkUrl({ token })
      .then((url) => {
        if (url) {
          window.location.href = url;
        } else {
          setError(
            "This invitation link cannot be used directly. Please use the link from your invitation email, or ask your admin to resend the invitation.",
          );
        }
      })
      .catch(() => {
        setError("Something went wrong. Please try again or contact your admin.");
      });
  }, [token, getClerkUrl, invitation]);

  if (!invitation) {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <XCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>This invitation link is invalid or has been removed.</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/sign-in" className={cn(buttonVariants({ variant: "outline" }))}>
              Sign in
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitation.isExpired || invitation.status === "expired") {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10">
              <Clock className="h-6 w-6 text-amber-500" />
            </div>
            <CardTitle>Invitation Expired</CardTitle>
            <CardDescription>
              This invitation has expired. Please contact your team admin for a new invite.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/" className={cn(buttonVariants({ variant: "outline" }))}>
              Go home
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitation.status === "accepted") {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            </div>
            <CardTitle>Already Accepted</CardTitle>
            <CardDescription>
              This invitation has already been accepted. If this was you, please sign in.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/sign-in" className={cn(buttonVariants())}>
              Sign in
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitation.status === "revoked") {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Invitation Revoked</CardTitle>
            <CardDescription>This invitation has been revoked by the team admin.</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/" className={cn(buttonVariants({ variant: "outline" }))}>
              Go home
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Invitation Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/" className={cn(buttonVariants({ variant: "outline" }))}>
              Go home
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Redirecting to Clerk invitation URL
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <p className="text-muted-foreground text-sm">
        Preparing your invitation to join {invitation.companyName}…
      </p>
    </div>
  );
}
